import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './pm-modal.style.js';
import { createPreventiveMaintenance, getAssetsByCompany, getUsersByCompany } from '@cmms/api';
import { Firestore } from 'firebase/firestore';
import { PMTemplateFormData } from '../../../types/pm.types.js';

@customElement('pm-modal')
export class PMModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Object })
  formData: PMTemplateFormData = {
    name: '',
    description: '',
    asset_id: '',
    freq_unit: 'months',
    freq_value: 1,
    anchor_date: Date.now(),
    grace_days: 0,
    default_assigned_to: '',
    default_priority_cat: 2,
    is_active: true
  };
  @property({ type: String }) companyId: string = '';
  @property({ type: Object }) firestore: Firestore = null as any;
  @state() private assets: any[] = [];
  @state() private users: any[] = [];
  @state() private isLoading = false;
  @state() private errorMessage = '';

  connectedCallback() {
    super.connectedCallback();
    this.loadAssetsAndUsers();
  }


  private async loadAssetsAndUsers() {
    try {
      this.isLoading = true;
      const [assetsResult, usersResult] = await Promise.all([
        getAssetsByCompany(this.firestore, this.companyId),
        getUsersByCompany(this.firestore, this.companyId)
      ]);

      if (assetsResult.error) {
        console.error('Error loading assets:', assetsResult.error);
      } else {
        this.assets = Array.isArray(assetsResult.data) ? assetsResult.data : [];
      }

      if (usersResult.error) {
        console.error('Error loading users:', usersResult.error);
      } else {
        this.users = Array.isArray(usersResult.data) ? usersResult.data : [];
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private handleInputChange = (e: Event, field: keyof PMTemplateFormData) => {
    const target = e.target as HTMLInputElement;
    if (field === 'freq_value' || field === 'grace_days' || field === 'default_priority_cat') {
      (this.formData as any)[field] = parseInt(target.value) || 0;
    } else if (field === 'is_active') {
      (this.formData as any)[field] = (target as HTMLInputElement).checked;
    } else {
      (this.formData as any)[field] = target.value;
    }
    this.requestUpdate();
  };

  private handleSelectChange = (e: CustomEvent, field: keyof PMTemplateFormData) => {
    const target = e.target as HTMLSelectElement;
    (this.formData as any)[field] = target.value;
    this.requestUpdate();
  };

  private handleSubmit = async (e: Event) => {
    e.preventDefault();
    
    if (!this.companyId) {
      this.errorMessage = 'Company ID is required';
      return;
    }
    
    if (!this.firestore) {
      this.errorMessage = 'Database connection is not available';
      return;
    }

    if (!this.formData.name.trim()) {
      this.errorMessage = 'PM name is required';
      return;
    }

    if (!this.formData.asset_id) {
      this.errorMessage = 'Asset selection is required';
      return;
    }

    try {
      this.isLoading = true;
      this.errorMessage = '';

      const pmData = {
        ...this.formData,
        company_id: this.companyId,
        created_at: Date.now(),
        updated_at: Date.now(),
        created_by: 'user', // TODO: Get from auth context
        updated_by: 'user',
        next_due_at: this.calculateNextDueDate()
      };

      const result = await createPreventiveMaintenance(this.firestore, pmData);

      if (!result.error) {
        this.dispatchEvent(new CustomEvent('pm-created', {
          detail: result.data,
          bubbles: true,
          composed: true
        }));
        
        this.closeModal();
      } else {
        this.errorMessage = result.error || 'Failed to create preventive maintenance';
      }
    } catch (error: any) {
      console.error('Error creating PM:', error);
      this.errorMessage = 'An unexpected error occurred';
    } finally {
      this.isLoading = false;
    }
  };

  private calculateNextDueDate(): number {
    const now = Date.now();
    const { freq_unit, freq_value, anchor_date } = this.formData;
    
    // Calculate milliseconds for the frequency
    let frequencyMs = 0;
    switch (freq_unit) {
      case 'days':
        frequencyMs = freq_value * 24 * 60 * 60 * 1000;
        break;
      case 'weeks':
        frequencyMs = freq_value * 7 * 24 * 60 * 60 * 1000;
        break;
      case 'months':
        frequencyMs = freq_value * 30 * 24 * 60 * 60 * 1000; // Approximate
        break;
    }
    
    // Find the next occurrence after the anchor date
    let nextDue = anchor_date + frequencyMs;
    while (nextDue <= now) {
      nextDue += frequencyMs;
    }
    
    return nextDue;
  }

  private closeModal() {
    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }));
    this.resetForm();
  }

  private resetForm() {
    this.formData = {
      name: '',
      description: '',
      asset_id: '',
      freq_unit: 'months',
      freq_value: 1,
      anchor_date: Date.now(),
      grace_days: 0,
      default_assigned_to: '',
      default_priority_cat: 2,
      is_active: true
    };
    this.errorMessage = '';
  }

  private getAssetName = (id: string) => {
    const asset = this.assets.find(a => a.id === id || a.$id === id || a.asset_id === id);
    return asset?.name || asset?.model?.name || `Asset ${id}`;
  };

  private getUserName = (id: string) => {
    const user = this.users.find(u => u.id === id || u.$id === id || u.user_id === id || u.uid === id);
    return user?.name || user?.model?.name || user?.email || `User ${id}`;
  };

  render() {
    if (!this.open) {
      return html``;
    }
    return html`
      <sl-dialog
        label="Add Preventive Maintenance"
        .open=${this.open}
        @sl-hide=${this.closeModal}
      >
        ${this.isLoading ? html`
          <div style="text-align: center; padding: 2rem;">
            <sl-spinner></sl-spinner>
            <p>Loading assets and users...</p>
          </div>
        ` : html`
          ${this.errorMessage ? html`
            <sl-alert variant="danger" open style="margin-bottom: 1rem;">
              <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
              ${this.errorMessage}
            </sl-alert>
          ` : ''}

          <form @submit=${this.handleSubmit} 
            @click=${(e: Event) => e.stopPropagation()} 
            @sl-input=${(e: Event) => e.stopPropagation()} 
            @sl-change=${(e: Event) => e.stopPropagation()}
            @sl-open-change=${(e: Event) => e.stopPropagation()}
            @sl-show=${(e: Event) => e.stopPropagation()}
            @sl-hide=${(e: Event) => e.stopPropagation()}
          >
            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="name">Name *</label>
              <sl-input
                id="name"
                name="name"
                placeholder="Enter PM name"
                value=${this.formData.name || ''}
                required
                @sl-input=${(e: Event) => this.handleInputChange(e, 'name')}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-icon name="wrench" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="description">Description</label>
              <sl-textarea
                id="description"
                name="description"
                placeholder="Enter PM description"
                value=${this.formData.description || ''}
                rows="3"
                @sl-input=${(e: Event) => this.handleInputChange(e, 'description')}
                @click=${(e: Event) => e.stopPropagation()}
              ></sl-textarea>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="asset_id">Asset *</label>
              <sl-select
                id="asset_id"
                name="asset_id"
                value=${this.formData.asset_id || ''}
                required
                @sl-change=${(e: CustomEvent) => this.handleSelectChange(e, 'asset_id')}
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-option value="">Select an asset</sl-option>
                ${this.assets.map(asset => html`
                  <sl-option value=${asset.id || asset.$id || asset.asset_id}>
                    ${this.getAssetName(asset.id || asset.$id || asset.asset_id)}
                  </sl-option>
                `)}
              </sl-select>
            </div>

            <div class="form-row">
              <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
                <label for="freq_value">Frequency Value *</label>
                <sl-input
                  id="freq_value"
                  name="freq_value"
                  type="number"
                  placeholder="1"
                  value=${this.formData.freq_value || 1}
                  min="1"
                  required
                  @sl-input=${(e: Event) => this.handleInputChange(e, 'freq_value')}
                  @click=${(e: Event) => e.stopPropagation()}
                ></sl-input>
              </div>

              <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
                <label for="freq_unit">Frequency Unit *</label>
                <sl-select
                  id="freq_unit"
                  name="freq_unit"
                  value=${this.formData.freq_unit || 'months'}
                  required
                  @sl-change=${(e: CustomEvent) => this.handleSelectChange(e, 'freq_unit')}
                  @sl-open-change=${(e: Event) => e.stopPropagation()}
                  @sl-show=${(e: Event) => e.stopPropagation()}
                  @sl-hide=${(e: Event) => e.stopPropagation()}
                  @click=${(e: Event) => e.stopPropagation()}
                >
                  <sl-option value="days">Days</sl-option>
                  <sl-option value="weeks">Weeks</sl-option>
                  <sl-option value="months">Months</sl-option>
                </sl-select>
              </div>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="grace_days">Grace Days</label>
              <sl-input
                id="grace_days"
                name="grace_days"
                type="number"
                placeholder="0"
                value=${this.formData.grace_days || 0}
                min="0"
                @sl-input=${(e: Event) => this.handleInputChange(e, 'grace_days')}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-icon name="clock" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="default_assigned_to">Default Assigned To</label>
              <sl-select
                id="default_assigned_to"
                name="default_assigned_to"
                value=${this.formData.default_assigned_to || ''}
                @sl-change=${(e: CustomEvent) => this.handleSelectChange(e, 'default_assigned_to')}
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-option value="">Select a user (optional)</sl-option>
                ${this.users.map(user => html`
                  <sl-option value=${user.id || user.$id || user.user_id || user.uid}>
                    ${this.getUserName(user.id || user.$id || user.user_id || user.uid)}
                  </sl-option>
                `)}
              </sl-select>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="default_priority_cat">Default Priority</label>
              <sl-select
                id="default_priority_cat"
                name="default_priority_cat"
                value=${this.formData.default_priority_cat || 2}
                @sl-change=${(e: CustomEvent) => this.handleSelectChange(e, 'default_priority_cat')}
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-option value="1">Low</sl-option>
                <sl-option value="2">Medium</sl-option>
                <sl-option value="3">High</sl-option>
                <sl-option value="4">Critical</sl-option>
              </sl-select>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <sl-checkbox
                ?checked=${this.formData.is_active}
                @sl-change=${(e: Event) => this.handleInputChange(e, 'is_active')}
              >
                Active
              </sl-checkbox>
            </div>

            <div class="form-actions">
              <sl-button
                variant="neutral"
                size="large"
                @click=${this.closeModal}
                ?disabled=${this.isLoading}
              >
                Cancel
              </sl-button>
              <sl-button
                variant="primary"
                size="large"
                type="submit"
                ?loading=${this.isLoading}
                ?disabled=${this.isLoading}
              >
                ${this.isLoading ? 'Creating...' : 'Create PM'}
              </sl-button>
            </div>
          </form>
        `}
      </sl-dialog>
    `;
  }

  static styles = styles;
}

declare global {
  interface HTMLElementTagNameMap {
    'pm-modal': PMModal;
  }
}
